import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useUserAuth } from '../../context/UserAuthContext';
import axios from 'axios';
import './Admin.css';

const API_BASE_URI = process.env.REACT_APP_API_BASE_URI;

const Settings = () => {
  const [settings, setSettings] = useState({
    allowPhotoUploads: true,
    allowWishes: true,
    maxPhotoSizeMB: 20,
    topPhotosLimit: 20
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user } = useUserAuth();
  const { t } = useTranslation();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError('');

      const token = await user.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API_BASE_URI}/admin/settings`, { headers });
      
      setSettings(response.data.settings || {});
      
    } catch (err) {
      console.error('Error fetching settings:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const token = await user.getIdToken();
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      };

      await axios.put(
        `${API_BASE_URI}/admin/settings`, 
        settings,
        { headers }
      );
      
      setSuccess(t('adminDashboard.settingsSaved'));
      
      // Clear success message after a few seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
      
    } catch (err) {
      console.error('Error saving settings:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // For checkboxes use the checked property, for other inputs use value
    let inputValue;
    
    if (type === 'checkbox') {
      inputValue = checked;
    } else if (type === 'number' || name === 'topPhotosLimit') {
      // For number inputs or the topPhotosLimit dropdown, convert to number
      inputValue = parseInt(value, 10);
      console.log(`Converting ${name} value ${value} to number: ${inputValue}`);
    } else {
      inputValue = value;
    }
    
    console.log(`Setting ${name} to:`, inputValue);
    
    setSettings({
      ...settings,
      [name]: inputValue
    });
  };

  if (loading) {
    return (
      <div className="text-center py-3">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">{t('loading')}</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div className="settings-management">
      <h2 className="mb-4">{t('adminDashboard.appSettings')}</h2>
      
      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}
      
      <Card>
        <Card.Body>
          <Form onSubmit={handleSaveSettings}>
            <Form.Group className="mb-4">
              <Form.Label>{t('adminDashboard.photoUploadSettings')}</Form.Label>
              <div className="mb-3">
                <Form.Check 
                  type="switch"
                  id="allow-photo-uploads"
                  name="allowPhotoUploads"
                  label={t('adminDashboard.allowPhotoUploads')}
                  checked={settings.allowPhotoUploads}
                  onChange={handleInputChange}
                />
                <Form.Text className="text-muted">
                  {t('adminDashboard.allowPhotoUploadsDesc')}
                </Form.Text>
              </div>
              
              <Form.Label>{t('adminDashboard.maxPhotoSize')}</Form.Label>
              <Form.Control
                type="number"
                name="maxPhotoSizeMB"
                value={settings.maxPhotoSizeMB}
                onChange={handleInputChange}
                min={1}
                max={20}
              />
              <Form.Text className="text-muted">
                {t('adminDashboard.maxPhotoSizeDesc')}
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label>{t('adminDashboard.topPhotosSettings')}</Form.Label>
              <Form.Select
                name="topPhotosLimit"
                value={settings.topPhotosLimit}
                onChange={handleInputChange}
                className="mb-2"
              >
                <option value="5">5 {t('adminDashboard.photos')}</option>
                <option value="10">10 {t('adminDashboard.photos')}</option>
                <option value="15">15 {t('adminDashboard.photos')}</option>
                <option value="20">20 {t('adminDashboard.photos')}</option>
                <option value="25">25 {t('adminDashboard.photos')}</option>
                <option value="30">30 {t('adminDashboard.photos')}</option>
              </Form.Select>
              <Form.Text className="text-muted mb-3 d-block">
                {t('adminDashboard.topPhotosDesc')}
              </Form.Text>
            </Form.Group>
            
            <Form.Group className="mb-4">
              <Form.Label>{t('adminDashboard.wishesSettings')}</Form.Label>
              <div className="mb-3">
                <Form.Check 
                  type="switch"
                  id="allow-wishes"
                  name="allowWishes"
                  label={t('adminDashboard.allowWishes')}
                  checked={settings.allowWishes}
                  onChange={handleInputChange}
                />
                <Form.Text className="text-muted">
                  {t('adminDashboard.allowWishesDesc')}
                </Form.Text>
              </div>
            </Form.Group>
            
            <div className="d-grid">
              <Button 
                variant="primary" 
                type="submit"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    {t('saving')}
                  </>
                ) : (
                  t('adminDashboard.saveSettings')
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default Settings;